/**
 * Модель навигации: группы и пункты с учётом прав.
 *
 * Одна модель на все места, где есть меню: боковая панель, полоса иконок,
 * нижняя панель телефона, лист «Ещё» и командная палитра. Пункт попадает
 * в модель, только если он выдан человеку, — ни одно меню не показывает
 * то, что откроется отказом.
 */

import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { CurrentUser } from '../types'
import type { IconName } from './Icon'
import { sectionPath, visibleSections } from '../finance/FinanceBlock'
import { insightsNavGroups } from '../insights/sections'
import { withViewTransition } from '../finance/motion'

export interface NavItem {
  key: string
  path: string
  label: string
  icon: IconName
  /** Разделы с периодом и городом: переход сохраняет ?p= и ?b=. */
  keepsFilters?: boolean
}

export interface NavGroup {
  key: 'finance' | 'summary' | 'sales' | 'stock' | 'reports' | 'data' | 'admin'
  title: string
  items: NavItem[]
}

export function navModel(user: CurrentUser): NavGroup[] {
  const finance = visibleSections(user)
  const groups: NavGroup[] = []

  if (finance.length > 0) {
    groups.push({
      key: 'finance',
      title: 'Финансы',
      items: finance.map((section) => ({
        key: `finance.${section.code}`,
        path: sectionPath(section.code),
        label: section.short,
        icon: section.icon,
        keepsFilters: true,
      })),
    })
  }

  // Подрядчику 1С отчёты не выданы: «/» для него перебрасывает в кабинет
  // загрузки, и пункт «Обзор» вёл бы туда же под чужим названием.
  const contractorOnly = user.reports.length === 0 && user.can_monitor_ingest
  if (!contractorOnly) {
    groups.push({
      key: 'summary',
      title: 'Сводка',
      items: [{ key: 'summary', path: finance.length > 0 ? '/summary' : '/', label: finance.length > 0 ? 'Краткая сводка' : 'Обзор', icon: 'summary' }],
    })
  }

  for (const group of insightsNavGroups(user)) {
    groups.push({
      key: group.group,
      title: group.title,
      items: group.items.map((item) => ({ key: item.path, path: item.path, label: item.short, icon: item.icon, keepsFilters: group.group === 'sales' })),
    })
  }

  // Ни одного отчёта — каталог пуст, пункт не нужен.
  if (user.reports.length > 0) {
    groups.push({ key: 'reports', title: 'Отчёты', items: [{ key: 'reports', path: '/reports', label: 'Дополнительные отчёты', icon: 'reports' }] })
  }

  const data: NavItem[] = []
  if (user.is_admin || user.can_monitor_ingest) data.push({ key: 'data', path: '/data', label: 'Загрузки и сверка', icon: 'data' })
  if (user.is_admin) data.push({ key: 'upload', path: '/upload', label: 'Загрузка Excel', icon: 'upload' })
  if (user.is_admin || user.can_monitor_ingest) data.push({ key: 'uploads', path: '/uploads', label: 'Загрузка данных', icon: 'download' })
  if (data.length > 0) groups.push({ key: 'data', title: 'Данные', items: data })

  if (user.is_admin) {
    groups.push({
      key: 'admin',
      title: 'Администрирование',
      items: [
        { key: 'access', path: '/admin/access', label: 'Матрица доступа', icon: 'access' },
        { key: 'users', path: '/admin/users', label: 'Пользователи', icon: 'user-cog' },
        { key: 'rules', path: '/admin/rules', label: 'Правила филиалов', icon: 'rules' },
        { key: 'finance-classes', path: '/admin/finance', label: 'Финансовая разметка', icon: 'pnl' },
        { key: 'tokens', path: '/admin/tokens', label: 'Токены 1С', icon: 'key' },
        { key: 'log', path: '/admin/log', label: 'Журналы', icon: 'log' },
      ],
    })
  }

  return groups
}

/** Активен ли пункт для текущего адреса. */
export function isActive(item: NavItem, pathname: string): boolean {
  if (item.path === '/reports') return pathname === '/reports' || pathname.startsWith('/reports/')
  if (item.path === '/admin/users') return pathname === '/admin/users' || pathname.startsWith('/admin/users/')
  return pathname === item.path
}

/**
 * Переход по пункту: разделы с периодом сохраняют ?p= и ?b=, смена
 * раздела идёт через View Transitions. Остальные — обычный переход.
 */
export function useNavGo() {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(
    (item: Pick<NavItem, 'path' | 'keepsFilters'>) => {
      let target = item.path
      if (item.keepsFilters) {
        const current = new URLSearchParams(location.search)
        const next = new URLSearchParams()
        for (const key of ['p', 'b']) {
          const value = current.get(key)
          if (value) next.set(key, value)
        }
        const query = next.toString()
        if (query) target = `${item.path}?${query}`
      }
      if (`${location.pathname}${location.search}` === target) return
      withViewTransition(() => navigate(target))
      window.scrollTo({ top: 0 })
    },
    [navigate, location.pathname, location.search],
  )
}
